/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import {
  $ReleaseFilters,
  GetReleasesData,
  GetReleasesResponse,
  ReleaseFilters,
  ReleaseResponse,
  ReleaseSortableFields,
} from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { usePaginatedQuery } from '@/hooks/query/usePaginatedQuery';
import { jsonSchemaToTypeMap } from '@/utils/filters';

export type UsePaginatedReleasesQueryParams = {
  params?: GetReleasesData;
  type?: 'inMemory' | 'queryParams';
  urlPrefix?: string;
  enabled?: boolean;
};

/**
 * Hook for fetching paginated releases with filtering, sorting and real-time updates.
 *
 * @param options Configuration object:
 *   - params: Optional query parameters (filters, pagination, sort)
 *   - type: Storage type - 'inMemory' or 'queryParams' for URL persistence
 *
 * @returns Object containing:
 *   - data: Paginated releases response
 *   - isLoading: Boolean for initial load state
 *   - isFetching: Boolean for any loading state
 *   - isError: Boolean for error state
 *   - error: Error object if request failed
 *   - updateQueryParams: Function to update filters, sort, and pagination
 *
 */
export const usePaginatedReleasesQuery = ({ params, type, urlPrefix, enabled }: UsePaginatedReleasesQueryParams) => {
  const filters = params?.filters || {};

  return usePaginatedQuery<
    GetReleasesResponse,
    ReleaseResponse,
    ReleaseFilters,
    ReleaseSortableFields,
    GetReleasesData
  >({
    apiCall: mgmtPlaneAPIClient.releases.getReleases.bind(mgmtPlaneAPIClient.releases),
    filterTypes: jsonSchemaToTypeMap($ReleaseFilters),
    urlPrefix,
    queryKey: 'releases',
    realtimeEvents: ['release:created', 'release:updated', 'release:deleted'],
    params: {
      filters,
      ...params,
    },
    type,
    enabled,
  });
};
